import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Input, Switch, TextField } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import * as Yup from 'yup';

import EditText from '../../components/editText';
import { showError, showToast } from '../../constants/toasts';
import MainContainer from '../../layout/MainContainer';
import {
  useAddBlogMutation,
  useLazyGetBlogByIdQuery,
  useUpdateBlogMutation,
} from '../../service/blog';
import { BlogData, CommonBody } from '../../types/General';
import { generateEncryptedKeyBody } from '../../utils/crypto';
import { UploadMedia } from '../../utils/mediaUpload';

const AddBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile_picture, setProfilePicture] = useState('');
  const [blogImage, setBlogImage] = useState('');

  const [description, setDescription] = useState(' ');
  const [isBlogScheduled, setIsBlogScheduled] = useState(true);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [blogDetail, setBlogDetail] = useState<BlogData | null>(null);
  const [addBlog] = useAddBlogMutation();
  const [updateBlog] = useUpdateBlogMutation();
  const [getDetail] = useLazyGetBlogByIdQuery();

  const label = { inputProps: { 'aria-label': 'Switch demo' } };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setProfilePicture('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setProfilePicture(imageUrl?.data?.image || '');
    }
  };

  const handleBlogUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setBlogImage('');
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;
    if (imageUrl?.data?.image) {
      setBlogImage(imageUrl?.data?.image || '');
    }
  };

  const getBlogDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await getDetail({
        id,
      }).unwrap();
      setLoading(false);

      if (response?.statusCode === 200) {
        setBlogDetail(response?.data);
        // formik.setFieldValue("title", response?.data?.title);
        setProfilePicture(response?.data?.authorImage);
        setBlogImage(response?.data?.image);
        setIsBlogScheduled(response?.data?.isBlogSchedule);
        setDescription(response?.data?.description);
        // formik.setFieldValue("authorName", response?.data?.authorName);
        // formik.setFieldValue("blogCategory", response?.data?.blogCategory);
      }
    } catch (error: any) {
      showError(error?.data?.message || '');
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      title: blogDetail?.title || '',
      blogCategory: blogDetail?.blogCategory || '',
      authorName: blogDetail?.authorName || '',
      date:
        blogDetail?.startDate && moment(blogDetail?.startDate).isValid()
          ? moment(blogDetail?.startDate)
          : null,
    },

    validationSchema: Yup.object({
      title: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
      blogCategory: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
      authorName: Yup.string()
        .required('This field is required')
        .min(2, 'Minimum 2 characters are required'),
      date: Yup.date()
        .required('Date is required')
        .min(moment().format('YYYY-MM-DDTHH:mm'), 'Date cannot be in the past'),
    }),
    onSubmit: async (values: any) => {
      formik.setSubmitting(true);
      if (description?.length <= 8) {
        showError('Description is required');
        return;
      }
      const body = {
        image: blogImage || '',
        title: values?.title || '',
        authorImage: profile_picture || '',
        blogCategory: values?.blogCategory || '',
        authorName: values?.authorName || '',
        description: description || '',
        isBlogSchedule: isBlogScheduled,
        ...(isBlogScheduled ? { startDate: moment(values.date).utc().format() } : {}),
      };

      if (id) {
        try {
          setLoading(true);
          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateBlog({
            body: encryptedBody,
            id,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Blog updated successfully');
            navigate('/blogs');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          console.error(error);
        }
      } else {
        try {
          setLoading(true);

          const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addBlog({
            body: encryptedBody,
          }).unwrap();
          setLoading(false);

          if (response?.statusCode === 200) {
            showToast('Blog added successfully');
            navigate('/blogs');
          }
        } catch (error: any) {
          showError(error?.data?.message || '');
          console.error(error);
        }
      }
    },
  });
  useEffect(() => {
    if (id) {
      getBlogDetail(id);
    }
  }, []);

  return (
    <MainContainer>
      <div className="main_title fdx">
        <div>
          <h1>{id ? 'Edit' : 'Add'} Blog</h1>
          <p>
            <span onClick={() => navigate('/dashboard')}>Dashboard</span> -{' '}
            <span onClick={() => navigate('/blogs')}>List of Blogs</span> - {id ? 'Edit' : 'Add'}{' '}
            Blog
          </p>
        </div>
        <div className="right_s align_right">
          <Button className="btn btn_orange" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="cards">
        <form className="form" onSubmit={formik.handleSubmit}>
          <div className="gap_p">
            <div className="control_group w_50">
              <label>Blog Image</label>
              <div className="upload_box_bnr">
                <label>
                  {blogImage && (
                    <IconButton className="crossIcon" onClick={() => setBlogImage('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img src={blogImage ? blogImage : '/static/images/picture.png'} alt="Upload" />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleBlogUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
              </div>
            </div>
            <div className="control_group w_50">
              <label>Schedule Blog?</label>
              <Switch
                {...label}
                checked={isBlogScheduled}
                onChange={() => setIsBlogScheduled(!isBlogScheduled)}
              />
            </div>
            <div className="control_group w_50">
              <label>Blog Title</label>
              <TextField
                variant="outlined"
                placeholder="Blog Title"
                fullWidth
                hiddenLabel
                name="title"
                id="title"
                className="text_field"
                inputProps={{ maxLength: 40 }}
                value={formik.values.title}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.title && formik.errors.title}
              />
            </div>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              {isBlogScheduled ? (
                <div className="control_group w_50">
                  <label>Select Date & Time</label>

                  <DateTimePicker
                    className="datepicker"
                    disablePast
                    value={formik.values.date ? moment(formik.values.date) : null}
                    onChange={(newValue) => {
                      formik.setFieldValue('date', newValue ? moment(newValue) : null);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        sx: {},
                        error: Boolean(formik.errors.date && formik.touched.date),
                        helperText: formik.touched.date && formik.errors.date,
                      },
                    }}
                  />
                </div>
              ) : null}
            </LocalizationProvider>

            <div className="control_group w_50">
              <label>Blog Category</label>
              <TextField
                variant="outlined"
                placeholder="Blog Category"
                fullWidth
                hiddenLabel
                name="blogCategory"
                id="blogCategory"
                className="text_field"
                inputProps={{ maxLength: 40 }}
                value={formik.values.blogCategory}
                onChange={(val) => {
                  if (val.target.value === ' ' || val.target.value === '.') {
                  } else {
                    formik.handleChange(val);
                  }
                }}
                onBlur={formik.handleBlur}
                helperText={formik.touched.blogCategory && formik.errors.blogCategory}
              />
            </div>

            <div className="control_group w_100">
              <label>Blog Description</label>
              {id ? (
                description?.length >= 8 ? (
                  <EditText content={description} setContent={setDescription} />
                ) : null
              ) : (
                <EditText content={description} setContent={setDescription} />
              )}
            </div>
          </div>
          <h2 style={{ margin: '10px 0' }}>Author Details</h2>
          <div className="control_group w_50">
            <label>Author Image</label>
            <div className="upload_box">
              <div className="upload_box">
                <label>
                  {profile_picture && (
                    <IconButton className="crossIcon" onClick={() => setProfilePicture('')}>
                      <CloseIcon />
                    </IconButton>
                  )}
                  <figure>
                    <img
                      src={profile_picture ? profile_picture : '/static/images/picture.png'}
                      alt="Upload"
                    />
                  </figure>
                  <IconButton className="uploadIcon">
                    <label htmlFor="icon-button-file-profile">
                      <Button component="span" className="upload_image_btn">
                        <img src="/static/images/edit2_icon.svg" alt="Upload" />
                        <Input
                          sx={{ display: 'none' }}
                          id="icon-button-file-profile"
                          type="file"
                          inputProps={{
                            accept: 'image/png,image/jpeg',
                          }}
                          onChange={(e: any) => {
                            handleImageUpload(e);
                            e.target.value = null;
                          }}
                        />
                      </Button>
                    </label>
                  </IconButton>
                </label>
                {error && !profile_picture ? <h6 className="err_msg">Image is required</h6> : null}
              </div>
            </div>
          </div>
          <div className="control_group w_50">
            <label>Author Name</label>
            <TextField
              variant="outlined"
              placeholder="Author Name"
              fullWidth
              hiddenLabel
              name="authorName"
              id="authorName"
              className="text_field"
              inputProps={{ maxLength: 40 }}
              value={formik.values.authorName}
              onChange={(val) => {
                if (val.target.value === ' ' || val.target.value === '.') {
                } else {
                  formik.handleChange(val);
                }
              }}
              onBlur={formik.handleBlur}
              helperText={formik.touched.authorName && formik.errors.authorName}
            />
          </div>

          <div className="form_end_btn">
            <Button
              className="btn btn_secondary"
              type="submit"
              onClick={() => setError(true)}

              // onClick={() => navigate("/blogs")}
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </MainContainer>
  );
};

export default AddBlog;
